<script>
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import multiMonthPlugin from '@fullcalendar/multimonth'
import rrulePlugin from '@fullcalendar/rrule'
import nlLocale from '@fullcalendar/core/locales/nl';
import EventCreate from "@/components/EventCreate.vue";
import EventDrag from "@/components/EventDrag.vue";
import EventResize from "@/components/EventResize.vue";
import EventRecurringModal from "@/components/EventRecurringModal.vue";
import CalendarSidebar from "@/components/CalendarSidebar.vue";
import {nextTick} from "vue";

export default {
  components: {
    CalendarSidebar,
    EventRecurringModal,
    EventResize,
    EventDrag,
    FullCalendar, // make the <FullCalendar> tag available
    EventCreate,
  },
  beforeMount() {
    // Set the default date
    this.calendarOptions.initialDate = this.getInitialDate();
    // Set the default view
    this.calendarOptions.initialView = this.getInitialView();
    // Set the weekends options
    this.setAgendaUserSettings();
  },
  mounted() {
    // Get the agendas
    this.getAgendas();
    // Restore the showAgendas setting from localStorage
    this.setShowAgendaDefaultValue();
  },
  data() {
    return {
      showAgendas: false,
      agendasLoading: true,
      eventsLoading: false,
      agendas: {
        users: [],
        color: [],
      },
      calendarOptions: {
        plugins: [rrulePlugin, dayGridPlugin, timeGridPlugin, multiMonthPlugin, interactionPlugin],
        locale: nlLocale,
        // timeZone: 'UTC',
        initialView: null,
        initialDate: null,
        editable: true,
        eventResizableFromStart: true, // Make events resizable from the start
        height: "100%",
        weekNumbers: true,
        slotLabelInterval: "01:00",
        scrollTime: "06:00:00",
        defaultTimedEventDuration: "00:30",
        snapDuration: "00:05",
        forceEventDuration: true,
        customButtons: {
          toggleAgendas: {
            text: 'Agenda\'s',
            click: () => {
              this.toggleAgendas();
            }
          },
          addEvent: {
            text: 'Activiteit toevoegen',
            click: this.handleCreateEvent
          }
        },
        headerToolbar: {
          left: 'prev,today,next,toggleAgendas',
          center: 'title',
          right: 'multiMonthYear,dayGridMonth,timeGridWeek,timeGridDay,addEvent' // user can switch between the views
        },
        slotLabelFormat: {
          hour: 'numeric',
          minute: '2-digit',
          omitZeroMinute: false,
          meridiem: 'short'
        },
        eventTimeFormat: { // like "14:30:00"
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false
        },
        views: {
          multiMonthYear: {
            displayEventTime: false,
          },
          dayGrid: {
            // options apply to dayGridMonth, dayGridWeek, and dayGridDay views
            dayHeaderFormat: { weekday: 'short' },
          },
          timeGrid: {
            // options apply to timeGridWeek and timeGridDay views
            dayHeaderFormat: { day: 'numeric', weekday: 'short' },
          },
          day: {
            // options apply to dayGridDay and timeGridDay views
          }
        },
        eventTextColor: 'rgba(255,255,255,1)',
        events: {
          url: '/api/calendar/events',
          method: 'GET',
          extraParams: {
            agendas_user: [],
            agendas_color: [],
          },
          failure: function() {
            alert('there was an error while fetching events');
          },
        },
        // Event triggered when dragging stops and the event has moved to a different day/time.
        eventDrop: this.handleEventDrop,
        eventResize: this.handleEventResize,
        dateClick: this.handleDateClick,
        eventClick: this.handleEventClick,
        loading: this.calendarIsLoading,
        // noinspection JSUnusedGlobalSymbols
        // Note: This function does get used. It is called by FullCalendar.
        viewDidMount: function (info) {
          // Save the default view
          localStorage.setItem("fcDefaultView", info.view.type);
        },
      }
    }
  },
  methods: {
    calendarIsLoading(isLoading) {
      this.eventsLoading = isLoading;
    },
    handleDateClick(arg) {
      // Show the dialog and prefill the values
      this.$refs.eventCreate.openDialog(arg);
    },
    handleCreateEvent() {
      // Show the dialog
      this.$refs.eventCreate.openDialog();
    },
    async handleEventDrop(eventDropInfo) {
      let event = eventDropInfo.event; // The new event after it was moved
      let extendedProps = event.extendedProps; // An object holding miscellaneous other properties specified in the Event Object.

      // Check if the event is recurring
      if (extendedProps.recurring) {
        // Show modal to ask if the user wants to update all events or just this one
        this.$refs.eventRecurringModal.openDialog(eventDropInfo);
      } else {
        // Update the event date
        let success = await this.$refs.eventDrag.changeEventDate(eventDropInfo);
        if (success === true) {
          // Show message
          this.$toast.add({
            severity: 'success',
            summary: 'Activiteit verplaatst',
            detail: 'De activiteit is verplaatst.',
            group: 'global',
            life: 4000
          });
        }
      }
    },
    async handleEventResize(eventResizeInfo) {
      let event = eventResizeInfo.event; // The new event after it was moved
      let extendedProps = event.extendedProps; // An object holding miscellaneous other properties specified in the Event Object.

      // Check if the event is recurring
      if (extendedProps.recurring) {
        // Show modal to ask if the user wants to update all events or just this one
        this.$refs.eventRecurringModal.openDialog(eventResizeInfo);
      } else {
        // Update the event date
        let success = await this.$refs.eventResize.resizeEventDate(eventResizeInfo);
        if (success === true) {
          // Show message
          this.$toast.add({
            severity: 'success',
            summary: 'Activiteit verplaatst',
            detail: 'De activiteit is verplaatst.',
            group: 'global',
            life: 4000
          });
        }
      }
    },
    handleEventClick(info) {
      let event = info.event; // The Event Object that was clicked
      // Trigger redirect to edit event
      this.redirectToEventEdit(event);
    },
    eventCreated() {
      // Reload events (do this do show the new event)
      this.getCalendarApi().refetchEvents()
    },
    /**
     * Get the fullcalender API
     */
    getCalendarApi() {
      return this.$refs.fullCalendar.getApi()
    },
    redirectToEventEdit(event) {
      // Get the ID of the event
      let id = event.id;
      // Get current calendar date
      let date = this.getCalendarApi().getDate();

      let yyyy = date.getFullYear();
      let mm = date.getMonth() + 1;
      let dd = date.getDate();
      let date_string = yyyy + '-' + (mm < 10 ? '0' : '') + mm + '-' + (dd < 10 ? '0' : '') + dd; // Zero pad the month and day

      let destination_url = encodeURIComponent('calendar?date=' + date_string);

      // TODO: Add selected agenda filters when redirecting back

      // Redirect user to the event
      window.location.href = '/event/' + id + '/edit?destination=' + destination_url; // TODO: Maybe open this in a modal with iframe instead of the window?
    },
    getInitialDate() {
      let date = new Date();
      let urlParams = new URLSearchParams(window.location.search);
      let dateParam = urlParams.get('date');
      if (dateParam) {
        date = new Date(dateParam);
      }
      return date;
    },
    getInitialView() {
      let view = localStorage.getItem("fcDefaultView");
      if (view === null) {
        view = "dayGridMonth";
      }
      return view;
    },
    /**
     * Set the agenda settings that the user has set in the settings.
     */
    setAgendaUserSettings() {
      // Get the settings from the drupalSettings
      let agendaSettings = drupalSettings.curiatAgenda;
      // Set the 'weekends' option
      this.calendarOptions.weekends = agendaSettings.showWeekends;
      this.calendarOptions.slotDuration = agendaSettings.slotDuration;
    },
    async toggleAgendas() {
      this.showAgendas = !this.showAgendas;
      // Store if agenda sidebar is shown in localStorage
      localStorage.setItem('showAgendas', JSON.stringify(this.showAgendas));
      // Make sure the DOM is updated
      await nextTick()
      // Update the size
      this.getCalendarApi().updateSize();
    },
    onAgendaChange(selectedAgendaIds) {
      // Set new selected agendas
      this.calendarOptions.events.extraParams.agendas_user = selectedAgendaIds.users ?? [];
      this.calendarOptions.events.extraParams.agendas_color = selectedAgendaIds.color ?? [];
      // Update the events
      this.getCalendarApi().refetchEvents();
    },
    /**
     * @returns {Promise<void>}
     */
    async getAgendas() {
      this.agendasLoading = true;
      // Get the agendas
      const response = await fetch('/api/calendar/agendas', {
        credentials: "include"
      })
      this.agendasLoading = false;
      const status_code = response.status;
      if (status_code !== 200) {
        // Show toast
        this.$toast.add({ severity: 'error', summary: 'Fout', detail: 'Kon de agendas niet laden.' });
      }
      // Parse the response
      let data = await response.json();
      // Set agendas
      this.agendas.users = data.users;
      this.agendas.color = data.colors;
    },
    setShowAgendaDefaultValue() {
      // Get the showAgendas setting from localStorage
      let showAgendas = localStorage.getItem('showAgendas');
      if (showAgendas) {
        this.showAgendas = JSON.parse(showAgendas);
      } else {
        // Default to false
        this.showAgendas = false;
      }
    }
  },
}
</script>

<template>

  <Toast position="top-center" />

  <div style="height: 6px" class="mb-3 loader">
    <ProgressBar mode="indeterminate" style="height: 6px" v-if="eventsLoading || agendasLoading"></ProgressBar>
  </div>

  <div class="flex gap-4 h-full">
    <div class="col-calendar-sidebar" v-show="showAgendas">
      <CalendarSidebar :agendas="agendas" @selectedAgendasChanged="onAgendaChange"></CalendarSidebar>
    </div>
    <div class="col-calendar-content flex-auto">
      <FullCalendar :options="calendarOptions" ref="fullCalendar" />
    </div>
  </div>

  <EventCreate ref="eventCreate" :agendas="agendas" @event-created="eventCreated"></EventCreate>
  <EventDrag ref="eventDrag"></EventDrag>
  <EventResize ref="eventResize"></EventResize>
  <EventRecurringModal ref="eventRecurringModal" @redirectToEventEdit="redirectToEventEdit"></EventRecurringModal>

</template>

<style>
 /* See agenda.scss for styling. Styling is here because we want the Gin/Bootstrap variables. */
</style>
